// ---------------------------------------------------------------------------
// Filters to API Parameters Converter
// ---------------------------------------------------------------------------
// Follows the same pattern as periodToDates() - pure function, no side effects.
// Converts Discovery filter state directly to API query parameters.
// Critical: Per-datasource mapping for demographic and content-specific dimensions.
// ---------------------------------------------------------------------------

import type { DataSourceId } from '@/components/v11/discovery/types';
import type { DiscoveryFilter, ApiFilterParams } from './types';
import { GENERATION_OPTIONS } from '@/components/v11/funnel/constants';

/**
 * Per-datasource mapping from frontend dimension key to backend API parameter name.
 *
 * CRITICAL: Content demographic filters (gender, country) use cross-view semantics
 * (audience/commenters), NOT the post author's attributes.
 *
 * Source of truth: Backend FormRequest classes
 * - ProfilesIndexRequest.php
 * - ContentIndexRequest.php
 * - CommentsIndexRequest.php
 */
const API_PARAM_MAP: Readonly<Record<DataSourceId, Readonly<Record<string, string>>>> = {
  profiles: {
    // Demographics
    platform: 'platform',
    gender: 'gender',
    age: 'age',
    country: 'country',
    ethnicity: 'ethnicity',
    lifestyle: 'lifestyle',
    industry: 'industry',
    followerTier: 'follower_tier',
    statusClass: 'status_class',
    continent: 'continent',
    // Booleans
    verified: 'verified',
    famous: 'famous',
    isPerson: 'is_person',
    processed: 'processed',
    // Sentiment & Moderation
    sentiment: 'sentiment',
    toxic: 'toxic',
    spam: 'spam',
    // Entities (search-mode)
    brand: 'brand',
    team: 'team',
    item: 'item',
    brandCategory: 'brandCategory',
    // Content dimensions (cross-view: filter profiles by content they interact with)
    channel: 'channel',
    hashtag: 'hashtag',
    emotion: 'emotion',
    mediaType: 'mediaType',
    category: 'category',
    post: 'post',
    language: 'language',
  },
  content: {
    // Platform & Content-specific (direct)
    platform: 'platform',
    category: 'category',
    mediaType: 'media_type',
    channel: 'channel',
    hashtag: 'hashtag',
    emotion: 'emotion',
    performance: 'performance',
    paidOrganic: 'paid_organic',
    // Audience demographics (cross-view via commenter profiles)
    gender: 'gender',
    country: 'country',
    // Cross-view demographics (via commenter profiles)
    age: 'age',
    ethnicity: 'ethnicity',
    followerTier: 'follower_tier',
    lifestyle: 'lifestyle',
    industry: 'industry',
    statusClass: 'status_class',
    // Entities
    brand: 'brand',
    team: 'team',
    item: 'item',
    post: 'post',
    brandCategory: 'brandCategory',
    // Author (drill-down from profiles)
    author: 'author_id',
    // Behaviour & Moderation (cross-view)
    language: 'language',
    sentiment: 'sentiment',
    toxic: 'toxic',
    spam: 'spam',
    // Booleans (cross-view via profiles)
    verified: 'verified',
    famous: 'famous',
    isPerson: 'is_person',
  },
  comments: {
    // Platform & Comment-specific (direct)
    platform: 'platform',
    emotion: 'emotion',
    language: 'language',
    // Author demographics (direct on comments view)
    gender: 'author_gender',
    age: 'author_age',
    country: 'author_country',
    // Cross-view demographics (via author profiles)
    ethnicity: 'ethnicity',
    followerTier: 'follower_tier',
    lifestyle: 'lifestyle',
    industry: 'industry',
    statusClass: 'status_class',
    // Moderation (direct)
    toxic: 'toxic',
    spam: 'spam',
    // Sentiment (special handling for min/max)
    sentiment: 'sentiment',
    // Entities & Content (cross-view)
    channel: 'channel',
    hashtag: 'hashtag',
    brand: 'brand',
    team: 'team',
    item: 'item',
    post: 'post_id',
    brandCategory: 'brandCategory',
    mediaType: 'mediaType',
    category: 'category',
    // Author (drill-down from profiles)
    author: 'author_id',
    // Booleans (cross-view via profiles)
    verified: 'verified',
    famous: 'famous',
    isPerson: 'is_person',
  },
  brands: {
    // Demographics (cross-view via profiles)
    platform: 'platform',
    gender: 'gender',
    age: 'age',
    country: 'country',
    ethnicity: 'ethnicity',
    followerTier: 'follower_tier',
    industry: 'industry',
    lifestyle: 'lifestyle',
    statusClass: 'status_class',
    // Booleans (cross-view via profiles)
    verified: 'verified',
    famous: 'famous',
    isPerson: 'is_person',
    // Direct (brand-specific)
    brandCategory: 'category',
    // Cross-view entities
    team: 'team',
    // Cross-view content/comments
    emotion: 'emotion',
    mediaType: 'mediaType',
    language: 'language',
    channel: 'channel',
    post: 'post',
    hashtag: 'hashtag',
    // Cross-view quality
    toxic: 'toxic',
    spam: 'spam',
    sentiment: 'sentiment',
  },
  teams: {
    // Demographics (cross-view via profiles)
    platform: 'platform',
    gender: 'gender',
    age: 'age',
    country: 'country',
    ethnicity: 'ethnicity',
    followerTier: 'follower_tier',
    industry: 'industry',
    lifestyle: 'lifestyle',
    statusClass: 'status_class',
    // Booleans (cross-view via profiles)
    verified: 'verified',
    famous: 'famous',
    isPerson: 'is_person',
    // Cross-view entities
    brand: 'brand',
    item: 'item',
    brandCategory: 'brandCategory',
    // Cross-view content/comments
    emotion: 'emotion',
    mediaType: 'mediaType',
    category: 'category',
    language: 'language',
    channel: 'channel',
    post: 'post',
    hashtag: 'hashtag',
    // Cross-view quality
    toxic: 'toxic',
    spam: 'spam',
    sentiment: 'sentiment',
  },
  hashtags: {},
  channels: {},
};

/**
 * Dimensions where values are 'yes'/'no' and must be converted to 'true'/'false' strings.
 * Backend uses filter_var($value, FILTER_VALIDATE_BOOLEAN) which parses 'true'/'false'.
 */
const BOOLEAN_DIMENSIONS = new Set([
  'verified',
  'famous',
  'isPerson',
  'processed',
  'toxic',
  'spam',
]);

/**
 * Convert Discovery filter state to API query parameters.
 *
 * Pure function with no side effects - same pattern as periodToDates().
 *
 * Critical: Uses per-datasource mapping to send correct parameter names.
 * - Profiles: gender → 'gender' (direct column)
 * - Content: gender → 'gender' (cross-view via commenter audience)
 * - Comments: gender → 'author_gender' (direct column on comment author)
 *
 * @param filters - Array of active filters
 * @param dataSource - Current datasource (determines API key mapping)
 * @returns Flat object ready to spread into axios params
 *
 * @example
 * const filters = [
 *   { dimension: 'gender', values: [2], valueLabels: ['female'] },
 *   { dimension: 'platform', values: [1, 3], valueLabels: ['Instagram', 'YouTube'] }
 * ]
 *
 * filtersToApiParams(filters, 'profiles')
 * // => { gender: [2], platform: [1, 3] }
 *
 * filtersToApiParams(filters, 'content')
 * // => { author_gender: [2], platform: [1, 3] }
 */
export function filtersToApiParams(
  filters: readonly DiscoveryFilter[],
  dataSource: DataSourceId
): ApiFilterParams {
  const params: ApiFilterParams = {};
  const keyMap = API_PARAM_MAP[dataSource];

  if (!keyMap) return params;

  for (const filter of filters) {
    const { dimension, values } = filter;

    // Skip empty filters
    if (values.length === 0) continue;

    // Get the API parameter name for this datasource
    const apiKey = keyMap[dimension];
    if (!apiKey) continue; // Dimension not supported for this datasource

    // Boolean dimensions: convert 'yes'/'no' to 'true'/'false' strings
    if (BOOLEAN_DIMENSIONS.has(dimension)) {
      if (values.length === 1) {
        params[apiKey] = values[0] === 'yes' ? 'true' : 'false';
      }
      continue;
    }

    // Sentiment special case for Comments: split into min/max
    if (dimension === 'sentiment' && dataSource === 'comments' && values.length === 2) {
      params['sentiment_min'] = values[0];
      params['sentiment_max'] = values[1];
      continue;
    }

    // Author / Post (comments): backend expects a single value, not an array
    if ((dimension === 'author' || (dimension === 'post' && apiKey === 'post_id')) && values.length === 1) {
      params[apiKey] = values[0];
      continue;
    }

    // Age/Generation: resolve generation keys to age interval IDs
    // Supports both generation keys ('gen_z') and direct integer IDs (for backward compatibility)
    if (dimension === 'age') {
      const ageIds = values.flatMap(val => {
        // Check if it's a generation key
        const genMatch = GENERATION_OPTIONS.find(g => g.key === val);
        if (genMatch) return Array.from(genMatch.ids);
        // If it's already an integer ID, pass through
        if (typeof val === 'number') return [val];
        // If it's a numeric string, convert and pass through
        const numVal = Number(val);
        if (!isNaN(numVal)) return [numVal];
        return [];
      });
      if (ageIds.length > 0) {
        params[apiKey] = ageIds;
      }
      continue;
    }

    // Standard array dimensions: pass values directly
    // The qs.stringify paramsSerializer in httpClient.ts will convert to Laravel format:
    // [1, 2, 3] -> "platform[]=1&platform[]=2&platform[]=3"
    params[apiKey] = Array.from(values);
  }

  return params;
}

/**
 * Check if any filters are active.
 */
export function hasActiveFilters(filters: readonly DiscoveryFilter[]): boolean {
  return filters.length > 0 && filters.some(f => f.values.length > 0);
}

/**
 * Count total number of active filter values across all dimensions.
 */
export function getActiveFilterCount(filters: readonly DiscoveryFilter[]): number {
  return filters.reduce((count, filter) => count + filter.values.length, 0);
}
